import React, { FunctionComponent, createContext, useContext, useEffect, useRef, useState } from "react";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import {
    createInitialState,
    TreeBrowserNode,
    TreeBrowserReadOnly,
    NodeServices,
    TreeBrowserComponent,
    useTreeBrowser,
    TreeBrowserContext,
    getNodeNestedLevel,
    isAllNodesSelected,
} from "@iventis/tree-browser";
import { useConstant, useDebounce, useObservableValue } from "@iventis/utilities";
import { defaultTreeBrowserSelection } from "@iventis/tree-browser/state/tree-browser-selection";
import { Button, Checkbox, IconButton, Popover, Radio, SxProps } from "@mui/material";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import styled from "@emotion/styled";
import debounce from "lodash.debounce";
import { Body2, Header6, InteractiveElement, inputHeight } from "@iventis/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Content } from "@iventis/translations";
import { Theme, useTheme } from "@emotion/react";
import { SearchBarComponent } from "@iventis/search";
import { renderVenueSelectedText } from "./schedule-helper-functions";
import { findHierarchyEntitiesByIds, findHierarchyEntitiesByType } from "./hierarchy.helpers";

const VenueSelectorContext = createContext<{ disable: boolean }>({ disable: false });

const rootVenueNode = "RootVenueNode";

interface VenueNode extends Omit<VenueLocation, "type">, TreeBrowserNode {
    venueType: VenueLocation["type"];
    type: "VenueNode";
    childNodes: [];
}

interface ClusterNode extends Omit<VenueLocation, "type">, TreeBrowserNode {
    venueType: VenueLocation["type"];
    type: "ClusterNode";
    childNodes: VenueNode[];
}
interface RootVenueNode extends TreeBrowserNode {
    type: typeof rootVenueNode;
    childNodes: ClusterNode[];
}

const venueNodeServices: NodeServices<RootVenueNode> = {
    addNode: () => null,
    deleteNodes: () => null,
    getNode: () => null,
    getTree: () => null,
    moveNodes: () => null,
    copyNodes: () => null,
    updateNode: () => null,
    getParents: () => null,
};

const convertHierarchyToNode = (hierarchy: Hierarchy<VenueLocation>, configIncludesClusters: boolean) => ({
    ...hierarchy.item,
    venueType: hierarchy.item.type,
    type: hierarchy.item.parentId == null && configIncludesClusters ? "ClusterNode" : "VenueNode",
    parentId: hierarchy.item.parentId ?? rootVenueNode,
    childNodes: hierarchy.children.map((v) => convertHierarchyToNode(v, configIncludesClusters)),
});

export const VenueSelectorButton: FunctionComponent<{
    selectedVenueLocations: Hierarchy<VenueLocation>[];
    flattenedVenueLocations: Hierarchy<VenueLocation>[];
    buttonStyle?: SxProps<Theme>;
    noneSelectedText?: string;
    children?: React.ReactNode;
}> = ({ selectedVenueLocations, flattenedVenueLocations, buttonStyle, noneSelectedText, children }) => {
    const theme = useTheme();
    const translate = useIventisTranslate();
    const ref = useRef<HTMLButtonElement>(null);
    const [state, setState] = useState(false);
    return (
        <>
            <Button
                variant="outlined"
                onClick={() => setState(true)}
                ref={ref}
                sx={{
                    boxSizing: "border-box",
                    marginLeft: "5px",
                    color: theme.primaryColors.altFocusText,
                    fontWeight: "bold",
                    borderColor: theme.primaryColors.altFocusBorder,
                    svg: { marginLeft: "7px" },
                    ...buttonStyle,
                    ":hover": {
                        backgroundColor: "transparent",
                        borderColor: theme.primaryColors.altFocusBorder,
                        ...buttonStyle?.[":hover"],
                    },
                }}
            >
                {!selectedVenueLocations?.length && noneSelectedText != null
                    ? noneSelectedText
                    : renderVenueSelectedText(selectedVenueLocations, flattenedVenueLocations, translate)}
                <FontAwesomeIcon icon="caret-down" />
            </Button>
            <StyledPopover
                sx={{ paddingTop: "1px" }}
                open={state}
                anchorEl={ref.current}
                onClose={() => setState(false)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                {children}
            </StyledPopover>
        </>
    );
};

const StyledPopover = styled(Popover)``;

/** Grouped list of all venues */
export const VenueSelector = ({
    venueLocations,
    selectedVenueLocations,
    singleSelectionOnly,
    configIncludesClusters,
    updateSelection: _updateSelection,
    isLoadingEventBlocks,
}: {
    venueLocations: Hierarchy<VenueLocation>[];
    selectedVenueLocations: Hierarchy<VenueLocation>[];
    singleSelectionOnly: boolean;
    configIncludesClusters: boolean;
    updateSelection: (selectedVenueLocations: Hierarchy<VenueLocation>[]) => void;
    isLoadingEventBlocks: boolean;
}) => {
    const translate = useIventisTranslate();
    const allVenues = useConstant(() =>
        findHierarchyEntitiesByType(venueLocations, configIncludesClusters ? venueLocations[0]?.children[0]?.item.type.id : venueLocations[0]?.item.type.id, (item) => item.type.id)
    );

    // Initial state
    const tree = useConstant<RootVenueNode>(() => ({
        id: rootVenueNode,
        type: rootVenueNode,
        name: rootVenueNode,
        childNodes: venueLocations.map((v) => convertHierarchyToNode(v, configIncludesClusters)),
    }));
    const initialSelectedIds = useConstant(() =>
        defaultTreeBrowserSelection<RootVenueNode>().fromSelectNodes({
            from: undefined,
            mainNodeId: tree.id,
            selectedNodeIds: [],
            nodes: selectedVenueLocations.map(({ item }) => item.id),
            tree,
            singleSelectionOnly,
            ctrlHeld: true,
        })
    );

    // Debounce updates
    const { current: updateSelectionDebounce } = React.useRef(debounce(_updateSelection, 300));
    const updateSelection = (selectedVenueLocations: Hierarchy<VenueLocation>[]) => {
        updateSelectionDebounce.cancel();
        updateSelectionDebounce(selectedVenueLocations);
    };

    // Initialise the tree browser
    const treeBrowser = useConstant(
        () =>
            new TreeBrowserReadOnly(
                createInitialState({
                    tree,
                    treeId: tree.id,
                    mainNodeId: tree.id,
                    isLoadingTree: false,
                    singleSelectionOnly,
                    selectedNodeIds: initialSelectedIds,
                    expandedNodeIds: singleSelectionOnly && selectedVenueLocations[0] != null ? [selectedVenueLocations[0].item?.parentId] : [],
                }),
                venueNodeServices,
                {
                    fromSelectNodes: (payload) => {
                        const selection = singleSelectionOnly ? (payload.nodes as string[]) : defaultTreeBrowserSelection<RootVenueNode>().fromSelectNodes(payload);
                        updateSelection(findHierarchyEntitiesByIds(allVenues, selection));
                        return selection;
                    },
                    fromSelectAll: (payload) => {
                        const selection = defaultTreeBrowserSelection<RootVenueNode>().fromSelectAll(payload);
                        updateSelection(findHierarchyEntitiesByIds(allVenues, selection));
                        return selection;
                    },
                }
            )
    );
    const state = useObservableValue(treeBrowser?.state, treeBrowser?.currentState);

    const allSelected = isAllNodesSelected(tree, state.selectedNodeIds);

    // Search logic
    const { value: search, debouncedValue: debouncedSearch, setValue: setSearch } = useDebounce(null, 700);
    useEffect(() => {
        if (debouncedSearch != null) {
            treeBrowser.search(debouncedSearch);
        }
    }, [debouncedSearch]);

    return (
        <TreeBrowserContext.Provider value={treeBrowser}>
            <VenueSelectorContext.Provider value={{ disable: isLoadingEventBlocks }}>
                <StyledContainer>
                    <SearchBarComponent
                        className="venue-search-bar"
                        // ToDo: Replace translation to common or schedule once traslations work
                        placeHolderText={translate(Content.map.nav.search)}
                        value={search ?? ""}
                        onValueChange={setSearch}
                        loading={false}
                    />
                    {!singleSelectionOnly && (
                        <StyledRow disabled={isLoadingEventBlocks} onClick={() => treeBrowser.toggleSelectAll()} level={0} paddingLeft={0}>
                            <Checkbox checked={allSelected} color="primary" disabled={isLoadingEventBlocks} />
                            {configIncludesClusters ? (
                                <Header6>({translate(Content.common.buttons.select_all)})</Header6>
                            ) : (
                                <Body2>({translate(Content.common.buttons.select_all)})</Body2>
                            )}
                        </StyledRow>
                    )}
                    {state.tree && (
                        <TreeBrowserComponent
                            node={state.filteredTree ?? state.tree}
                            openNodes={state?.expandedNodeIds ?? []}
                            displayRootNode={false}
                            components={{ VenueNode: VenueRow, ClusterNode: VenueRow }}
                        />
                    )}
                </StyledContainer>
            </VenueSelectorContext.Provider>
        </TreeBrowserContext.Provider>
    );
};

const StyledContainer = styled.div`
    position: relative;
    width: fit-content;
    .venue-search-bar {
        margin: 10px;
    }
`;

const VenueRow = ({ node }: { node: VenueNode | ClusterNode }) => {
    const { disable } = useContext(VenueSelectorContext);
    const [{ expandedNodeIds, selectedNodeIds, tree, singleSelectionOnly }, treeBrowserContext] = useTreeBrowser(
        TreeBrowserContext,
        node.id,
        "expandedNodeIds",
        "selectedNodeIds",
        "tree",
        "singleSelectionOnly"
    );
    const selected = selectedNodeIds.includes(node.id);
    const level = getNodeNestedLevel(tree, node);
    return (
        <>
            <StyledRow
                disabled={disable}
                onClick={() => (node.type !== "VenueNode" && singleSelectionOnly ? treeBrowserContext.toggleExpandNode(node) : treeBrowserContext.toggleSelectNodes([node.id]))}
                level={level}
                paddingLeft={node.type === "VenueNode" && level > 0 && singleSelectionOnly ? "20px" : 0}
            >
                {singleSelectionOnly ? (
                    node.type === "VenueNode" && <Radio size="small" checked={selected} color="default" disabled={disable} />
                ) : (
                    <Checkbox disabled={disable} checked={selected} />
                )}
                <div className="venue-row-indent" />
                {/* If it's a cluster, always show. If its a venue, don't show if venue is top level or selection is single only */}
                {(node.type === "ClusterNode" || (node.type === "VenueNode" && !(level === 0 || singleSelectionOnly))) && (
                    <div className="expand-collapse-cluster">
                        {/* If no children then don't show the caret */}
                        {node.childNodes?.length > 0 && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    treeBrowserContext.toggleExpandNode(node);
                                }}
                                disabled={disable}
                            >
                                <FontAwesomeIcon icon={expandedNodeIds.includes(node.id) ? "chevron-down" : "chevron-right"} fontSize="14px" />
                            </IconButton>
                        )}
                    </div>
                )}
                {node.type === "VenueNode" && <Body2>{node.name}</Body2>}
                {node.type === "ClusterNode" && <Header6>{node.name}</Header6>}
            </StyledRow>
        </>
    );
};

const StyledRow = styled(InteractiveElement)<{ level: number; paddingLeft: string | 0 }>`
    display: flex;
    align-items: center;
    width: 100%;
    height: ${inputHeight};
    padding-right: 20px;
    padding-left: ${(props) => props.paddingLeft};
    :hover {
        background-color: ${({ theme }) => theme.shades.four};
    }
    .venue-row-indent {
        padding-left: ${(props) => props.level * 15}px;
    }
    .expand-collapse-cluster {
        width: 40px;
        height: 40px;
        padding-right: 10px;
        button {
            height: 100%;
            width: 100%;
        }
    }
    .MuiSvgIcon-root {
        color: ${({ theme }) => theme.primaryColors.altFocusText};
    }
`;
